import { useMemo, useCallback } from "react";
import type { ShipmentUI } from "../../store/shipmentSlice";
import { ShipmentPropertyNames } from "@services/shipments/types";

// Helper function to get property value from shipment
const getShipmentPropertyValue = (
  shipment: ShipmentUI,
  propertyName: ShipmentPropertyNames,
): string => {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === propertyName,
  );
  if (matchingProperties.length === 0) return "";
  return matchingProperties[0].value;
};

// Sort shipments based on column and direction
const sortShipmentsImpl = (
  shipments: ShipmentUI[],
  columnName: string,
  direction: "asc" | "desc",
): ShipmentUI[] => {
  return [...shipments].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    // Handle different column types
    if (columnName === "createdAt" || columnName === "updatedAt") {
      // Date columns
      aValue = new Date(a[columnName as keyof ShipmentUI] as any).getTime();
      bValue = new Date(b[columnName as keyof ShipmentUI] as any).getTime();
    } else if (columnName === "eta") {
      // ETA is a property that might be a date string
      const aEta = getShipmentPropertyValue(a, "eta");
      const bEta = getShipmentPropertyValue(b, "eta");

      // Empty values go to bottom
      if (!aEta && !bEta) return 0;
      if (!aEta) return 1;
      if (!bEta) return -1;

      aValue = new Date(aEta).getTime();
      bValue = new Date(bEta).getTime();
    } else {
      // Property columns (booking_number, shipper, consignee, etc.)
      aValue =
        getShipmentPropertyValue(a, columnName as ShipmentPropertyNames) || "";
      bValue =
        getShipmentPropertyValue(b, columnName as ShipmentPropertyNames) || "";

      // Empty values go to bottom
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Case-insensitive string comparison
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

export function useSortShipments(
  shipments: ShipmentUI[],
  sortConfig: { columnName: string; direction: "asc" | "desc" } | null,
) {
  // Memoize the sorting function to prevent unnecessary re-renders
  const sortShipments = useCallback(
    (
      shipments: ShipmentUI[],
      columnName: string,
      direction: "asc" | "desc",
    ) => {
      return sortShipmentsImpl(shipments, columnName, direction);
    },
    [],
  );

  // Memoize the sorted shipments
  const sortedShipments = useMemo(() => {
    if (!sortConfig) {
      return shipments;
    }

    return sortShipments(
      shipments,
      sortConfig.columnName,
      sortConfig.direction,
    );
  }, [shipments, sortConfig, sortShipments]);

  return sortedShipments;
}
